import { useState, useMemo } from "react";
import { MOCK_SESSIONS, getUserById } from "../lib/mockData";
import { MapPin, Monitor, Users, Calendar, Shield, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "../lib/useLanguage";
import LogoSwap from "../components/LogoSwap";
import LanguageToggle from "../components/LanguageToggle";
import { CATEGORY_COLORS } from "../lib/skills";
import { toast } from "sonner";
import { motion } from "framer-motion";
import moment from "moment";

const CATS = ["Toutes", "Langues", "Études", "Digital", "CV / Emploi", "Sport", "Créatif", "Vie pratique", "Projets"];
const FORMATS_F = ["Tous", "En ligne", "Lieu public"];
const LEVELS_F = ["Tous", "Débutant", "Intermédiaire", "Tous niveaux"];

export default function Sessions() {
  const t = useT();
  const [cat, setCat] = useState("Toutes");
  const [fmt, setFmt] = useState("Tous");
  const [lvl, setLvl] = useState("Tous");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return MOCK_SESSIONS.filter(s => {
      if (cat !== "Toutes" && s.category !== cat) return false;
      if (fmt !== "Tous" && s.format !== fmt) return false;
      if (lvl !== "Tous" && s.level !== lvl) return false;
      if (search && !s.title.toLowerCase().includes(search.toLowerCase()) && !s.skill.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [cat, fmt, lvl, search]);

  return (
    <div className="max-w-lg mx-auto px-4 pt-4">
      <div className="flex items-center justify-between mb-4">
        <LogoSwap size="sm" />
        <LanguageToggle />
      </div>
      <h1 className="text-xl font-extrabold mb-0.5">{t("nav_sessions")}</h1>
      <p className="text-sm text-muted-foreground mb-4">Places limitées. Rejoins une session.</p>

      {/* Search */}
      <input type="text" value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Rechercher une session..."
        className="w-full bg-card border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none mb-3" />

      {/* Category chips */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide mb-3">
        {CATS.map(c => (
          <button key={c} onClick={() => setCat(c)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${cat === c ? "text-white border-transparent" : "bg-card border-border text-muted-foreground"}`}
            style={cat === c ? { backgroundColor: "#FF6B4A", borderColor: "#FF6B4A" } : {}}>
            {c}
          </button>
        ))}
      </div>

      {/* Format + Level filters */}
      <div className="flex gap-2 mb-4">
        <Select value={fmt} onValueChange={setFmt}>
          <SelectTrigger className="flex-1 rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{FORMATS_F.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={lvl} onValueChange={setLvl}>
          <SelectTrigger className="flex-1 rounded-xl h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{LEVELS_F.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <p className="text-xs text-muted-foreground mb-3">{filtered.length} session{filtered.length !== 1 ? "s" : ""}</p>

      {/* Sessions */}
      <div className="space-y-3">
        {filtered.map((s, i) => {
          const host = getUserById(s.hostId);
          const spotsLeft = s.capacity - s.participants;
          const colors = CATEGORY_COLORS[s.category] || CATEGORY_COLORS["Digital"];
          return (
            <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="bg-card rounded-2xl border border-border overflow-hidden">
              <div className="h-1" style={{ backgroundColor: colors.accent }} />
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0 mr-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>{s.category}</span>
                      <span className={`text-xs font-semibold ${spotsLeft <= 1 ? "text-red-500" : "text-green-600"}`}>
                        {spotsLeft <= 0 ? t("session_full") : `${spotsLeft} ${t("session_places")}`}
                      </span>
                    </div>
                    <h3 className="font-bold text-foreground text-base leading-snug">{s.title}</h3>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-semibold text-foreground">{moment(s.date).format("D MMM")}</div>
                    <div className="text-xs text-muted-foreground">{moment(s.date).format("HH:mm")}</div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-3">{s.description}</p>

                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground mb-3">
                  <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{s.zone}</span>
                  <span className="flex items-center gap-1"><Monitor className="h-3 w-3" />{s.format}</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" />{s.participants}/{s.capacity}</span>
                  <span className="flex items-center gap-1">📚 {s.level}</span>
                  {s.language && <span className="flex items-center gap-1">🌐 {s.language}</span>}
                </div>

                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                  <Shield className="h-3 w-3" style={{ color: "#FF6B4A" }} />
                  <span>{t("session_safety")}</span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {host?.photo && <img src={host.photo} alt="" className="h-7 w-7 rounded-xl object-cover" />}
                    <span className="text-sm font-medium">{host?.firstName}</span>
                  </div>
                  <Button size="sm" className="rounded-full text-xs px-5" disabled={spotsLeft <= 0}
                    style={spotsLeft > 0 ? { backgroundColor: "#B9F85A", color: "#101828" } : {}}
                    onClick={() => toast.info(t("error_login"))}>
                    {t("session_join")}
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}